import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { AbsenceResponse } from "../contracts/AbsenceResponse";
import { CreateAbsenceRequest } from "../contracts/CreateAbsenceRequest";
import { UpdateAbsenceRequest } from "../contracts/UpdateAbsenceRequest";
import { Absence } from "../models/Absence";
import { AbsenceResponseMapper } from "../mappers/AbsenceResponseMapper";

const mapper = new AbsenceResponseMapper();

/** Loads a page of the organization's absences, optionally filtered by member and overlap window. */
export const fetchAbsences = createAsyncThunk<
  { items: Absence[]; totalCount: number; page: number },
  { memberId?: string; from?: string; to?: string; page?: number; pageSize?: number },
  { rejectValue: ApiErrorResponse }
>("absences/fetch", async ({ memberId, from, to, page = 1, pageSize = 20 }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedResult<AbsenceResponse>>("/absences", {
      params: { memberId, from, to, page, pageSize },
    });
    return {
      items: mapper.toAbsences(response.data.items),
      totalCount: response.data.totalCount,
      page: response.data.page,
    };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load absences"));
  }
});

/** Loads every absence overlapping [from, to] for the availability overview grid. */
export const fetchAbsencesRange = createAsyncThunk<
  Absence[],
  { from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("absences/fetchRange", async ({ from, to }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AbsenceResponse[]>("/absences/overlapping", {
      params: { from, to },
    });
    return mapper.toAbsences(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load absences"));
  }
});

/** Loads the absences touching one day — feeds the attendance "away" badges. */
export const fetchAbsencesForDay = createAsyncThunk<
  { date: string; items: Absence[] },
  { date: string },
  { rejectValue: ApiErrorResponse }
>("absences/fetchForDay", async ({ date }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AbsenceResponse[]>("/absences/overlapping", {
      params: { from: date, to: date },
    });
    return { date, items: mapper.toAbsences(response.data) };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load absences"));
  }
});

export const createAbsence = createAsyncThunk<
  Absence,
  CreateAbsenceRequest,
  { rejectValue: ApiErrorResponse }
>("absences/create", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AbsenceResponse>("/absences", request);
    return mapper.toAbsence(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to record the absence"));
  }
});

export const updateAbsence = createAsyncThunk<
  Absence,
  { id: string; request: UpdateAbsenceRequest },
  { rejectValue: ApiErrorResponse }
>("absences/update", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<AbsenceResponse>(`/absences/${id}`, request);
    return mapper.toAbsence(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to update the absence"));
  }
});

/** Deletes an absence. Returns the removed absence's id. */
export const deleteAbsence = createAsyncThunk<string, string, { rejectValue: ApiErrorResponse }>(
  "absences/delete",
  async (absenceId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/absences/${absenceId}`);
      return absenceId;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to delete the absence"));
    }
  },
);
